import { useState, useCallback, useEffect } from 'react';

/**
 * useChatNavigation - Hook for managing chat message navigation
 * 
 * Provides Vim-style navigation through chat messages with focus management
 * and smooth scrolling using Virtuoso's native API.
 * 
 * @param {Array} messages - Array of message objects
 * @param {React.RefObject} virtuosoRef - Ref to the Virtuoso component
 */
export const useChatNavigation = (messages = [], virtuosoRef = null) => {
  const [focusedMessageIndex, setFocusedMessageIndex] = useState(-1);
  
  // Reset focus when messages change significantly
  useEffect(() => {
    if (messages.length === 0) {
      setFocusedMessageIndex(-1);
    } else if (focusedMessageIndex >= messages.length) {
      setFocusedMessageIndex(messages.length - 1);
    }
  }, [messages.length, focusedMessageIndex]);
  
  // Scroll to a specific index using Virtuoso's API
  const scrollToIndex = useCallback((index, options = {}) => {
    const ref = virtuosoRef?.current;
    if (!ref || messages.length === 0) return;
    
    const { align = 'start', behavior = 'smooth', offset } = options;
    const clamped = Math.max(0, Math.min(index, messages.length - 1));
    
    const location = offset != null
      ? { index: clamped, align, behavior, offset }
      : { index: clamped, align, behavior };
    
    ref.scrollToIndex(location);
  }, [virtuosoRef, messages.length]);
  
  // Navigate to next message
  const focusNextMessage = useCallback(() => {
    if (messages.length === 0) return;
    
    const next = focusedMessageIndex < 0 
      ? 0 
      : Math.min(focusedMessageIndex + 1, messages.length - 1);
    
    setFocusedMessageIndex(next);
    scrollToIndex(next, { align: 'start', behavior: 'smooth', offset: -60 });
  }, [messages.length, focusedMessageIndex, scrollToIndex]);
  
  // Navigate to previous message
  const focusPreviousMessage = useCallback(() => {
    if (messages.length === 0) return;
    
    const prev = focusedMessageIndex < 0 
      ? messages.length - 1 
      : Math.max(focusedMessageIndex - 1, 0);
    
    setFocusedMessageIndex(prev);
    scrollToIndex(prev, { align: 'start', behavior: 'smooth', offset: -60 });
  }, [messages.length, focusedMessageIndex, scrollToIndex]);
  
  // Jump to first message
  const focusFirstMessage = useCallback(() => {
    if (messages.length === 0) return;
    
    setFocusedMessageIndex(0);
    scrollToIndex(0, { align: 'start', behavior: 'smooth', offset: -60 });
  }, [messages.length, scrollToIndex]);
  
  // Jump to last message and scroll to bottom
  const focusLastMessage = useCallback(() => {
    if (messages.length === 0) return;
    
    const lastIndex = messages.length - 1;
    setFocusedMessageIndex(lastIndex);
    
    // Scroll to bottom with end alignment
    virtuosoRef?.current?.scrollToIndex({ 
      index: lastIndex, 
      align: 'start', 
      behavior: 'smooth' 
    });
  }, [messages.length, virtuosoRef]);
  
  // Jump to previous user message
  const focusPreviousUserMessage = useCallback(() => {
    if (messages.length === 0) return;
    
    let index = focusedMessageIndex < 0 ? messages.length - 1 : focusedMessageIndex - 1;
    
    while (index >= 0) {
      if (messages[index].isUser) {
        setFocusedMessageIndex(index);
        scrollToIndex(index, { align: 'start', behavior: 'smooth', offset: -60 });
        return;
      }
      index--;
    }
    
    // If no previous user message found, go to first message
    focusFirstMessage();
  }, [messages, focusedMessageIndex, scrollToIndex, focusFirstMessage]);
  
  // Jump to next user message
  const focusNextUserMessage = useCallback(() => {
    if (messages.length === 0) return;
    
    let index = focusedMessageIndex < 0 ? 0 : focusedMessageIndex + 1;
    
    while (index < messages.length) {
      if (messages[index].isUser) {
        setFocusedMessageIndex(index);
        scrollToIndex(index, { align: 'start', behavior: 'smooth', offset: -60 });
        return;
      }
      index++;
    }
    
    // If no next user message found, go to last message
    focusLastMessage();
  }, [messages, focusedMessageIndex, scrollToIndex, focusLastMessage]);
  
  // Clear focus (exit navigation mode)
  const clearFocus = useCallback(() => {
    setFocusedMessageIndex(-1);
  }, []);
  
  // Check if a specific message is focused
  const isMessageFocused = useCallback((index) => {
    return focusedMessageIndex === index;
  }, [focusedMessageIndex]);
  
  // Clear focus when any other element gains focus
  useEffect(() => {
    const handleFocusIn = () => {
      if (focusedMessageIndex >= 0) {
        clearFocus();
      }
    };
    
    document.addEventListener('focusin', handleFocusIn);
    return () => document.removeEventListener('focusin', handleFocusIn);
  }, [focusedMessageIndex, clearFocus]);
  
  return {
    // State
    focusedMessageIndex,
    isNavigationActive: focusedMessageIndex >= 0,
    
    // Actions
    focusNextMessage,
    focusPreviousMessage,
    focusFirstMessage,
    focusLastMessage,
    focusPreviousUserMessage,
    focusNextUserMessage,
    clearFocus,
    
    // Helpers
    isMessageFocused,
    
    // Navigation info
    hasPrevious: focusedMessageIndex > 0,
    hasNext: focusedMessageIndex < messages.length - 1,
    totalMessages: messages.length,
  };
};
